/**
 * Stock availability — "has the kitchen 86'd this?" — for the things a guest
 * can order: items and modifiers. The stock-state companion of
 * `channelVisibility.ts`, which answers the neighbouring question of whether a
 * thing is OFFERED on a channel at all.
 *
 * WHY THIS FILE EXISTS. The question had three storage shapes and the write
 * side and the read side had each picked a different one:
 *
 *   • `alwaysAvailable` + `quantityRemaining` — what BOTH operator surfaces
 *     write. Management's Availability radio (`ItemModal`, `ModifierModal`) and
 *     the POS availability dialog (`ItemAvailabilityCubit`,
 *     `ModifierAvailabilityCubit`) both store `alwaysAvailable: false` +
 *     `quantityRemaining: 0` and nothing else. Neither has ever written
 *     `isSuspended` or `stockInfo`.
 *   • `isSuspended` + `suspendedUntil` — legacy, and
 *   • `stockInfo.status` / `stockInfo.quantity` — both written ONLY by the
 *     automatic depletion path (`stockManagementService`), and only once a till
 *     sale drives the count to zero.
 *
 * Server-side checkout read the last two. So an item that sold out THROUGH THE
 * TILL was refused online, and an item 86'd BY HAND was invisible to online
 * ordering — and every 86 an operator performs is the second kind. Measured on
 * House of Pho production 2026-09-19: BANH MI was 86'd, the POS refused it, and
 * an online take-out order for it was charged and printed to the expediter
 * (order #10, 1:27 PM EDT). The owner pulled the item out of its menu group by
 * hand to stop the next customer.
 *
 * 🔴 ABSENT IS AVAILABLE. `alwaysAvailable` absent means the entity tracks no
 * stock — the ordinary case for almost every menu row ever written. Reading a
 * missing field as "out of stock" would 86 an entire menu in one deploy. Every
 * clause below is therefore an EXPLICIT `=== false` / `=== true` / a status
 * this code knows by name.
 *
 * ⚠️ `alwaysAvailable === false` IS the opt-in to counting, so once it is set,
 * a missing or null `quantityRemaining` means NONE LEFT, not "untracked" —
 * that is exactly what the operator's "Out of stock" radio writes, and the POS
 * reads it the same way (`Item.isAvailable` in `apps/pos`).
 *
 * Pure functions over the stored shape, so they answer identically for a raw
 * Firestore document and for a hydrated model instance.
 */

import type { StockStatus } from './types';
import type { IItemIngredientStock } from './ingredientAvailability';

/** The stock-bearing fields an item and a modifier share. */
export interface StockFields {
  /** Legacy suspension flag. */
  readonly isSuspended?: boolean;
  /** When a suspension lapses. Absent means the suspension is indefinite. */
  readonly suspendedUntil?: unknown;
  /** `false` opts this entity into quantity tracking. */
  readonly alwaysAvailable?: boolean;
  /** The count left, when `alwaysAvailable === false`. */
  readonly quantityRemaining?: number | null;
  /**
   * A MODIFIER's stock status — flat. Items carry the same fact nested under
   * `stockInfo.status` instead, and both are read below.
   *
   * ⚠️ THE ASYMMETRY IS LOAD-BEARING, not an inconsistency to tidy away. The
   * item↔modifier 86 mirror (`apps/functions/src/menus/availabilityMirror.ts`)
   * TRANSLATES between the two rather than copying, because writing
   * `stockStatus` onto an item — or `stockInfo` onto a modifier — succeeds,
   * changes the document, 86s nothing, and never terminates, since the target
   * never comes to agree and each write re-fires the other's trigger. A reader
   * that knows only ONE of the two shapes sees half the 86s.
   */
  readonly stockStatus?: StockStatus;
  /** An ITEM's richer stock record — nested. */
  readonly stockInfo?: {
    readonly status?: StockStatus;
    readonly quantity?: number;
    readonly suspendedUntil?: unknown;
  };
  /**
   * An ITEM's ingredient state — server-written by the ingredient-availability
   * reconciler (`ingredientAvailability.ts`). `blocked` means a VITAL
   * ingredient is out. Absent on modifiers and on every item no reconcile has
   * touched.
   */
  readonly ingredientStock?: IItemIngredientStock | null;
}

/** The statuses that mean "not sellable right now". */
const UNSELLABLE_STATUSES: ReadonlySet<string> = new Set([
  'OUT_OF_STOCK',
  'DISCONTINUED',
]);

/**
 * A stored instant as a `Date`, whatever shape it arrived in — a hydrated
 * `Date`, a raw Firestore `Timestamp`, or an ISO string off a JSON payload.
 * Returns `undefined` for anything it cannot read, which callers treat as "no
 * expiry given" rather than guessing an instant.
 */
function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  if (value && typeof value === 'object') {
    const ts = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof ts.toDate === 'function') {
      try {
        const d = ts.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined;
      } catch {
        return undefined;
      }
    }
    const seconds = ts.seconds ?? ts._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
  }
  return undefined;
}

/**
 * Whether a suspension recorded by `flag` is still in force. An ABSENT
 * `until` means indefinite — the shape `stockManagementService` writes when it
 * auto-suspends a depleted entity, and the shape an operator's plain 86 leaves.
 * Reading "no expiry" as "already lapsed" is how an indefinite 86 becomes
 * orderable, so the absent case must stay suspended.
 */
function isSuspensionInForce(
  flag: boolean | undefined,
  until: unknown,
  now: Date,
): boolean {
  if (flag !== true) return false;
  const expiry = toDate(until);
  return expiry === undefined || expiry > now;
}

/**
 * Whether this entity opts into quantity tracking. Only an explicit `false`
 * does — see ABSENT IS AVAILABLE above.
 */
function countsStock(entity: StockFields): boolean {
  return entity.alwaysAvailable === false;
}

/**
 * How many are left, or `undefined` when nothing counts them. Where both the
 * operator's count and the depletion path's count exist they should agree; the
 * SMALLER wins, because over-promising stock is the failure that reaches a
 * guest as a cancelled order.
 */
export function remainingStock(entity: StockFields): number | undefined {
  const counts: number[] = [];
  if (countsStock(entity)) {
    // Opted into counting: a missing count means none left, not "untracked".
    counts.push(
      typeof entity.quantityRemaining === 'number' ? entity.quantityRemaining : 0,
    );
  }
  if (typeof entity.stockInfo?.quantity === 'number') {
    counts.push(entity.stockInfo.quantity);
  }
  return counts.length ? Math.min(...counts) : undefined;
}

/**
 * Whether the kitchen can sell this at all right now — the one 86 question,
 * read across all three storage shapes.
 *
 * Deliberately says nothing about `isActive`, `isArchived` or channel switches:
 * those are separate questions with their own predicates, and folding them in
 * here is how one surface starts answering a question it was never asked.
 */
export function isInStock(entity: StockFields, now: Date = new Date()): boolean {
  if (countsStock(entity)) {
    const left = entity.quantityRemaining;
    if (typeof left !== 'number' || left <= 0) return false;
  }
  if (isSuspensionInForce(entity.isSuspended, entity.suspendedUntil, now)) {
    return false;
  }
  // BOTH shapes — the modifier's flat `stockStatus` and the item's nested
  // `stockInfo.status`. Reading one family only is what the mirror exists to
  // warn about; see `StockFields.stockStatus`.
  for (const status of [entity.stockStatus, entity.stockInfo?.status]) {
    if (typeof status === 'string' && UNSELLABLE_STATUSES.has(status)) return false;
  }
  // `stockInfo.suspendedUntil` carries no companion flag — its PRESENCE is the
  // whole claim — so an absent value is no suspension, not an indefinite one.
  const stockSuspendedUntil = toDate(entity.stockInfo?.suspendedUntil);
  if (stockSuspendedUntil && stockSuspendedUntil > now) return false;
  // A vital ingredient is out. The fourth shape, read HERE so every reader of
  // the 86 question (checkout, storefront, `Item.isAvailable`, the marketplace
  // transformers) honours it without a copy of its own.
  if (entity.ingredientStock?.blocked === true) return false;
  return true;
}

/**
 * Whether `quantity` of this entity can be sold right now: in stock at all, and
 * enough of it. An untracked entity has no ceiling.
 */
export function hasStockFor(
  entity: StockFields,
  quantity: number,
  now: Date = new Date(),
): boolean {
  if (!isInStock(entity, now)) return false;
  const left = remainingStock(entity);
  return left === undefined || left >= quantity;
}
